import { CloudAccountType } from '@repo/api-gw-sdk';

import { Wizard } from '@/components/wizard/Wizard';
import { useWorkspace } from '@/contexts/useWorkspace';

import {
  awsConnectRestoreAccountFlow,
  awsConnectSourceAccountFlow,
  awsConnectSourceOrganizationFlow,
} from './awsConnectFlows';
import type { AwsConnectRestoreAccountState } from './connectAwsRestoreAccount';
import type { AwsConnectSourceAccountState } from './connectAwsSourceAccount';
import type { AwsConnectOrganizationUnitState } from './connectAwsSourceOrganizationUnit';

export function AwsConnectAccountWizard({
  onCreated,
  accountType,
  onboardingFlow = false,
  selectedScope,
}: {
  onCreated: (accountId: string) => Promise<void>;
  accountType: CloudAccountType;
  onboardingFlow?: boolean;
  selectedScope?: 'account' | 'organization';
}) {
  const { rightPanel } = useWorkspace();

  const onFinish = async (
    state:
      | AwsConnectSourceAccountState
      | AwsConnectOrganizationUnitState
      | AwsConnectRestoreAccountState
  ) => {
    const id =
      'eonAccountId' in state && state.eonAccountId
        ? state.eonAccountId
        : 'accountId' in state && state.accountId
          ? state.accountId
          : 'managementAccountId' in state && state.managementAccountId
            ? state.managementAccountId
            : '';
    await onCreated(id);
    return true;
  };

  if (accountType === CloudAccountType.Restore) {
    return (
      <Wizard<AwsConnectRestoreAccountState>
        hideCloseButton={false}
        onAbort={() => rightPanel.setIsOpen(false)}
        hideBackArrow={true}
        onFinish={onFinish}
        flow={[awsConnectRestoreAccountFlow]}
        title='Connect restore account'
        initialState={{
          accountId: '',
          roleName: 'EonRestoreAccountRole',
          deploymentMethod: 'cloudformation',
        }}
      />
    );
  }

  if (accountType === CloudAccountType.Source) {
    if (selectedScope === 'organization') {
      return (
        <Wizard<AwsConnectOrganizationUnitState>
          hideCloseButton={false}
          onAbort={() => rightPanel.setIsOpen(false)}
          hideBackArrow={true}
          onFinish={onFinish}
          flow={[awsConnectSourceOrganizationFlow]}
          title='Connect source organizational unit'
          initialState={{
            onboardingFlow,
            organizationId: '',
            managementAccountId: '',
          }}
        />
      );
    }

    return (
      <Wizard<AwsConnectSourceAccountState>
        hideCloseButton={false}
        onAbort={() => rightPanel.setIsOpen(false)}
        hideBackArrow={true}
        onFinish={onFinish}
        flow={[awsConnectSourceAccountFlow]}
        title='Connect source account'
        initialState={{
          onboardingFlow,
          accountId: '',
          roleName: 'EonSourceAccountRole',
          deploymentMethod: 'cloudformation',
        }}
      />
    );
  }
}
